// YERİ: src/app/domains/site/sections/commerce.sections.ts
// Ticari bölümler — Hizmet Listesi, Fiyat Tablosu, Ürün Listesi. Config'ten okur
// (şema-tabanlı form ile düzenlenir; JSON gerekmez). Tema CSS vars ile renklenir.

import { Component, OnChanges, computed, inject, input, signal } from '@angular/core';
import { SiteChromeText } from '../core/site-chrome-text';
import { ShopProduct, TenantWebService } from '../core/tenant-web.service';
import { SiteRoot } from '../core/site-root';
import { MIcon } from '@/app/shared/ui/m-icon';
import { SiteConfig, cfg } from './section.model';

// ── Hizmet Listesi ──────────────────────────────────────────────────────────
@Component({
  selector: 'sec-services-grid',
  standalone: true,
  imports: [MIcon],
  template: `
    <section id="services" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-7xl px-6 text-center lg:px-8">
        @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
        <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
        @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        <div class="mt-12 grid gap-6 text-left sm:grid-cols-2 lg:grid-cols-3">
          @for (s of items(); track $index) {
            <div class="rounded-2xl border border-neutral-200 p-6 transition hover:shadow-lg dark:border-neutral-800">
              @if (s.icon) { <span class="flex size-12 items-center justify-center rounded-xl text-white" style="background-color:var(--site-primary)"><m-icon [name]="s.icon" /></span> }
              <h3 class="mt-4 text-lg font-bold text-neutral-900 dark:text-white">{{ s.title }}</h3>
              @if (s.description) { <p class="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-neutral-400">{{ s.description }}</p> }
              @if (s.price) { <p class="mt-3 text-lg font-bold" style="color:var(--site-primary)">{{ s.price }}</p> }
            </div>
          }
        </div>
      </div>
    </section>
  `,
})
export class ServicesGridSection {
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', 'Hizmetler'));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.sundugumuzHizmetler')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected items = computed<{ icon?: string; title: string; description?: string; price?: string }[]>(() => {
    const raw = this.config()['items'];
    return Array.isArray(raw) && raw.length ? raw.filter(i => i?.title) : [
      { icon: 'briefcase', title: 'Hizmet 1', description: this.text.t('fb.hizmetAciklamasiBurayaGelir') },
      { icon: 'shield', title: 'Hizmet 2', description: this.text.t('fb.hizmetAciklamasiBurayaGelir') },
      { icon: 'star', title: 'Hizmet 3', description: this.text.t('fb.hizmetAciklamasiBurayaGelir') },
    ];
  });
}

// ── Fiyat Tablosu ───────────────────────────────────────────────────────────
@Component({
  selector: 'sec-pricing-table',
  standalone: true,
  imports: [MIcon],
  template: `
    <section id="pricing" class="bg-neutral-50 py-24 dark:bg-neutral-900">
      <div class="mx-auto max-w-7xl px-6 text-center lg:px-8">
        @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
        <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
        @if (subtitle()) { <p class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
        <div class="mt-12 grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3">
          @for (p of plans(); track $index) {
            <div class="flex flex-col rounded-2xl border bg-white p-7 text-left dark:bg-neutral-950"
              [class]="isOn(p.highlighted) ? 'border-2 shadow-xl' : 'border-neutral-200 dark:border-neutral-800'"
              [style.border-color]="isOn(p.highlighted) ? 'var(--site-primary)' : null">
              <h3 class="text-lg font-bold text-neutral-900 dark:text-white">{{ p.name }}</h3>
              <p class="mt-3"><span class="font-[family-name:var(--site-head-font)] text-4xl font-bold" style="color:var(--site-primary)">{{ p.price }}</span>@if (p.period) { <span class="text-sm text-neutral-500">/{{ p.period }}</span> }</p>
              <ul class="mt-6 flex-1 space-y-3">
                @for (f of p.features || []; track $index) {
                  <li class="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300"><m-icon name="check" size="sm" class="mt-0.5 shrink-0" style="color:var(--site-primary)" /> {{ f }}</li>
                }
              </ul>
              @if (p.ctaText) {
                <a [href]="p.ctaUrl || '#'" class="ws-btn mt-7 inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-semibold transition hover:opacity-90"
                  [class.text-white]="isOn(p.highlighted)"
                  [style.background-color]="isOn(p.highlighted) ? 'var(--site-accent)' : 'transparent'"
                  [style.color]="isOn(p.highlighted) ? '#fff' : 'var(--site-primary)'"
                  [style.border]="isOn(p.highlighted) ? null : '1px solid var(--site-primary)'">{{ p.ctaText }}</a>
              }
            </div>
          }
        </div>
      </div>
    </section>
  `,
})
export class PricingTableSection {
  protected text = inject(SiteChromeText);
  config = input<SiteConfig>({});
  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('fb.fiyatlandirma')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.sizeUygunPaketiSecin')));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));
  protected plans = computed<{ name: string; price: string; period?: string; features?: string[]; ctaText?: string; ctaUrl?: string; highlighted?: string }[]>(() => {
    const raw = this.config()['plans'];
    return Array.isArray(raw) && raw.length ? raw.filter(p => p?.name) : [
      { name: this.text.t('fb.baslangic'), price: '₺0', period: 'ay', features: [this.text.t('fb.ozellik1'), this.text.t('fb.ozellik2')], ctaText: this.text.t('fb.sec') },
      { name: 'Pro', price: '₺199', period: 'ay', features: [this.text.t('fb.tumOzellikler'), this.text.t('fb.oncelikliDestek')], ctaText: this.text.t('fb.sec'), highlighted: 'true' },
      { name: 'Kurumsal', price: 'Teklif', features: [this.text.t('fb.ozelCozumler')], ctaText: this.text.t('fb.iletisim') },
    ];
  });
  protected isOn(v?: string) { return ['true', 'evet', '1'].includes(String(v ?? '').toLowerCase()); }
}

// ── Ürün Listesi ────────────────────────────────────────────────────────────
//
// İKİ KAYNAK:
//   'erp'    → ERP kataloğundan canlı okur (fiyat, stok, görsel sunucudan).
//   'manual' → editörde elle yazılan liste (eski davranış).
//
// Varsayılan: config'te 'source' YOKSA ve elle yazılmış kalem VARSA 'manual'.
// Böylece bu değişiklikten önce yayınlanmış sayfalar birebir aynı kalır; yeni
// (boş) bölüm ise doğrudan ERP'den beslenir. Açık seçim her zaman kazanır.
//
// Fiyat SUNUCUDA hesaplanıp gelir — burada tekrar hesaplanmaz. Vitrinde
// gösterilen tutar ile siparişte tahsil edilen tutar aynı kaynaktan.
@Component({
  selector: 'sec-product-list',
  standalone: true,
  template: `
    <section id="products" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="text-center">
          @if (eyebrow()) { <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p> }
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
        </div>

        @if (loading()) {
          <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            @for (i of skeletons; track i) {
              <div class="animate-pulse overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800">
                <div class="aspect-square bg-neutral-100 dark:bg-neutral-800"></div>
                <div class="space-y-2 p-4">
                  <div class="h-3 w-3/4 rounded bg-neutral-100 dark:bg-neutral-800"></div>
                  <div class="h-3 w-1/3 rounded bg-neutral-100 dark:bg-neutral-800"></div>
                </div>
              </div>
            }
          </div>
        } @else if (items().length === 0) {
          <p class="mt-12 text-center text-sm text-neutral-500">{{ emptyText() }}</p>
        } @else {
          <div class="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            @for (p of items(); track p.key) {
              <a [href]="p.url || '#'" class="group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 transition hover:shadow-lg dark:border-neutral-800">
                <div class="relative aspect-square overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                  @if (p.image) { <img [src]="p.image" [alt]="p.name" class="h-full w-full object-cover transition group-hover:scale-105" /> }
                  @if (p.soldOut) {
                    <span class="absolute left-3 top-3 rounded-full bg-neutral-900/80 px-2.5 py-1 text-[11px] font-semibold text-white">{{ soldOutText() }}</span>
                  } @else if (p.lowStock) {
                    <span class="absolute left-3 top-3 rounded-full bg-amber-500/90 px-2.5 py-1 text-[11px] font-semibold text-white">{{ lowStockText() }}</span>
                  }
                </div>
                <div class="flex flex-1 flex-col p-4">
                  <h3 class="text-sm font-semibold text-neutral-900 dark:text-white">{{ p.name }}</h3>
                  @if (p.description) { <p class="mt-1 line-clamp-2 text-xs text-neutral-500">{{ p.description }}</p> }
                  @if (p.price) { <p class="mt-2 text-base font-bold" style="color:var(--site-primary)">{{ p.price }}</p> }
                </div>
              </a>
            }
          </div>
        }
      </div>
    </section>
  `,
})
export class ProductListSection implements OnChanges {
  protected text = inject(SiteChromeText);
  private svc = inject(TenantWebService);
  /** Ürün bağlantısı: kiracı alan adında '/urun/{id}', platformda '/site/{clientId}/urun/{id}'. */
  private siteRoot = inject(SiteRoot);

  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected readonly skeletons = [0, 1, 2, 3];
  protected loading = signal(false);
  private erpProducts = signal<ShopProduct[]>([]);

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', this.text.t('fb.urunler')));
  protected title = computed(() => cfg(this.config(), 'title', this.text.t('fb.oneCikanUrunler')));
  private chrome = inject(SiteChromeText);

  // Varsayılanlar SİTENİN DİLİNDEN geliyor, sabit Türkçeden değil: bunlar
  // kiracının yazdığı içerik değil, ziyaretçiye görünen arayüz metni.
  // Fransızca gezen ziyaretçi "Tükendi" görmemeli.
  protected emptyText = computed(() =>
    cfg(this.config(), 'emptyText', '') || this.chrome.t('sh.noProducts'));
  protected soldOutText = computed(() =>
    cfg(this.config(), 'soldOutText', '') || this.chrome.t('pr.soldOut'));
  protected lowStockText = computed(() =>
    cfg(this.config(), 'lowStockText', '') || this.chrome.t('pr.lowStock'));

  private manualItems = computed<{ image?: string; name: string; price?: string; description?: string; url?: string }[]>(() => {
    const raw = this.config()['items'];
    return Array.isArray(raw) ? raw.filter(i => i?.name) : [];
  });

  /** Açık seçim > (elle kalem varsa manual) > erp. */
  protected source = computed<'erp' | 'manual'>(() => {
    const explicit = String(cfg(this.config(), 'source', '')).toLowerCase();
    if (explicit === 'erp' || explicit === 'manual') return explicit;
    return this.manualItems().length ? 'manual' : 'erp';
  });

  protected items = computed(() => {
    if (this.source() === 'manual') {
      return this.manualItems().map((p, i) => ({
        key: 'm' + i,
        name: p.name,
        description: p.description,
        price: p.price,
        image: p.image,
        url: p.url,
        soldOut: false,
        lowStock: false,
      }));
    }

    const limit = Number(cfg(this.config(), 'limit', '8')) || 8;
    return this.erpProducts().slice(0, limit).map(p => ({
      key: 'e' + p.id,
      name: p.name,
      description: p.description ?? undefined,
      // Fiyat sunucudan sayı olarak gelir; para birimini burada ekliyoruz.
      price: p.price != null ? `${p.price} ${p.currency ?? ''}`.trim() : undefined,
      image: p.imageUrl ?? undefined,
      url: this.siteRoot.path(this.clientId(), 'urun', p.id),
      soldOut: !p.inStock,
      lowStock: p.lowStock,
    }));
  });

  ngOnChanges(): void {
    if (this.source() !== 'erp') return;
    const id = this.clientId();
    if (!id) return;

    const raw = cfg(this.config(), 'categoryId', '');
    const categoryId = raw ? Number(raw) : null;

    this.loading.set(true);
    this.svc.getProducts(id, { categoryId: Number.isFinite(categoryId) ? categoryId : null }).subscribe({
      next: list => { this.erpProducts.set(list); this.loading.set(false); },
      // Sessiz düş: vitrin ziyaretçisine hata gösterme, bölüm boş kalsın.
      error: () => { this.erpProducts.set([]); this.loading.set(false); },
    });
  }
}
